import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Button } from '../common/Catalyst/button';
import {
  AmenityApprovalMode,
  AmenityBookingMode,
  AmenityEligibility,
  AmenityFeeType,
  AmenityLateCancelPolicy,
  AmenityReturn,
  AmenityUpdate,
} from '../clients/rust-api-client';
import {
  APPROVAL_MODE_LABELS,
  BOOKING_MODE_LABELS,
  BOOKING_MODES,
  ELIGIBILITY_LABELS,
  FEE_TYPE_LABELS,
  LATE_CANCEL_POLICY_LABELS,
  amenityToUpdate,
  formatDuration,
  formatMinute,
  isValidFee,
  optionalHours,
  optionalInt,
  reservationErrorMessage,
} from '../reservation/reservationMeta';
import {
  checkboxClass,
  helpClass,
  inputClass,
  labelClass,
} from '../reservation/formStyles';
import { ErrorAlert } from '../common/Custom/ErrorAlert';
import { FieldPicker } from '../common/Custom/FieldPicker';

/**
 * The amenity's settings tabs that are one `PUT /amenities/{id}`: details
 * and rules, booking limits, and the fee. Each starts from
 * `amenityToUpdate(amenity)` — the update is a **full replacement**, so a
 * form that sent only its own fields would reset the others to defaults.
 */
interface FormProps {
  amenity: AmenityReturn;
  readOnly: boolean;
  onSave: (update: AmenityUpdate) => Promise<void>;
}

/** Save button, error and "Saved" for one form. */
const useSave = (onSave: (u: AmenityUpdate) => Promise<void>) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const run = async (update: AmenityUpdate) => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await onSave(update);
      setSaved(true);
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't save the amenity."));
    } finally {
      setSaving(false);
    }
  };
  return { saving, error, saved, run };
};

const SaveRow: React.FC<{ saving: boolean; saved: boolean; error: string | null; disabled?: boolean }> = ({
  saving,
  saved,
  error,
  disabled,
}) => (
  <div className="space-y-2">
    <ErrorAlert>{error}</ErrorAlert>
    <div className="flex items-center gap-3">
      <Button color="sky" type="submit" disabled={saving || disabled}>
        {saving ? 'Saving…' : 'Save'}
      </Button>
      {saved && <span className="text-[12px] text-fg-muted">Saved.</span>}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Details & rules
// ---------------------------------------------------------------------------

export const AmenityDetailsForm: React.FC<FormProps> = ({ amenity, readOnly, onSave }) => {
  const [name, setName] = useState(amenity.name);
  const [description, setDescription] = useState(amenity.description ?? '');
  const [location, setLocation] = useState(amenity.location ?? '');
  const [rules, setRules] = useState(amenity.rules_text ?? '');
  const [bookingMode, setBookingMode] = useState<AmenityBookingMode>(amenity.booking_mode);
  const [approvalMode, setApprovalMode] = useState<AmenityApprovalMode>(amenity.approval_mode);
  const [active, setActive] = useState(amenity.is_active);
  const [sortOrder, setSortOrder] = useState(String(amenity.sort_order));
  const save = useSave(onSave);

  useEffect(() => {
    setName(amenity.name);
    setDescription(amenity.description ?? '');
    setLocation(amenity.location ?? '');
    setRules(amenity.rules_text ?? '');
    setBookingMode(amenity.booking_mode);
    setApprovalMode(amenity.approval_mode);
    setActive(amenity.is_active);
    setSortOrder(String(amenity.sort_order));
  }, [amenity]);

  const sort = optionalInt(sortOrder);
  const valid = name.trim().length > 0 && sort !== null && !Number.isNaN(sort);

  return (
    <form
      className="max-w-2xl space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid || readOnly) return;
        save.run({
          ...amenityToUpdate(amenity),
          name: name.trim(),
          description: description.trim() || null,
          location: location.trim() || null,
          rules_text: rules.trim() || null,
          booking_mode: bookingMode,
          approval_mode: approvalMode,
          is_active: active,
          sort_order: sort ?? 0,
        });
      }}
    >
      <fieldset disabled={readOnly} className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className={labelClass}>
            Name
            <input value={name} onChange={(e) => setName(e.target.value)} className={inputClass} />
          </label>
          <label className={labelClass}>
            Location
            <input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g. Behind the pool house"
              className={inputClass}
            />
          </label>
        </div>
        <label className={labelClass}>
          Description
          <textarea
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={clsx(inputClass, 'resize-y')}
          />
        </label>
        <label className={labelClass}>
          Rules households see before booking
          <textarea
            rows={5}
            value={rules}
            onChange={(e) => setRules(e.target.value)}
            placeholder="e.g. Children under 14 must be supervised by an adult resident. Music off by 10 PM."
            className={clsx(inputClass, 'resize-y')}
          />
          <span className={helpClass}>
            Write rules about conduct and supervision, never about age — an age-based ban on a pool or
            clubhouse can breach the Fair Housing Act.
          </span>
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className={labelClass}>
            Booking
            <FieldPicker
              size="sm"
              label="Booking"
              value={bookingMode}
              onChange={(v) => setBookingMode(v as AmenityBookingMode)}
              disabled={readOnly}
              options={BOOKING_MODES.map((m) => ({ value: m, label: BOOKING_MODE_LABELS[m] }))}
            />
            {bookingMode !== amenity.booking_mode && (bookingMode === 'shared_capacity' || bookingMode === 'overnight') && (
              <span className={clsx(helpClass, 'font-normal')}>
                {bookingMode === 'shared_capacity'
                  ? 'Set the headcount on the Booking limits tab after saving.'
                  : 'Set check-in and check-out times on the Booking limits tab after saving.'}
              </span>
            )}
          </div>
          <div className={labelClass}>
            Approval
            <FieldPicker
              size="sm"
              label="Approval"
              value={approvalMode}
              onChange={(v) => setApprovalMode(v as AmenityApprovalMode)}
              disabled={readOnly || bookingMode === 'info_only'}
              options={(['auto', 'manual'] as const).map((m) => ({ value: m, label: APPROVAL_MODE_LABELS[m] }))}
            />
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-6">
          <label className="flex items-center gap-2 text-[13px] text-fg-secondary">
            <input
              type="checkbox"
              className={checkboxClass}
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
            />
            Active — households can see and book it
          </label>
          <label className={labelClass}>
            Sort order
            <input
              inputMode="numeric"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value.replace(/\D/g, ''))}
              className={clsx(inputClass, 'w-20')}
            />
          </label>
        </div>
      </fieldset>
      {!readOnly && <SaveRow {...save} disabled={!valid} />}
    </form>
  );
};

// ---------------------------------------------------------------------------
// Booking limits
// ---------------------------------------------------------------------------

const INCREMENTS = [15, 30, 60, 90, 120, 240];
/** Check-in and check-out choices: every half hour. */
const TIMES = Array.from({ length: 48 }, (_, i) => i * 30);
const timeOptions = TIMES.map((m) => ({ value: String(m), label: formatMinute(m) }));

export const AmenityLimitsForm: React.FC<FormProps> = ({ amenity, readOnly, onSave }) => {
  const s = (n: number | null | undefined) => (n === null || n === undefined ? '' : String(n));
  const [increment, setIncrement] = useState(amenity.slot_increment_minutes);
  const [minDuration, setMinDuration] = useState(amenity.min_duration_minutes);
  const [maxDuration, setMaxDuration] = useState(amenity.max_duration_minutes);
  const [buffer, setBuffer] = useState(s(amenity.buffer_minutes));
  const [leadDays, setLeadDays] = useState(s(amenity.max_lead_days));
  const [noticeHours, setNoticeHours] = useState(s(amenity.min_notice_hours));
  const [cancelHours, setCancelHours] = useState(s(amenity.cancel_deadline_hours));
  const [party, setParty] = useState(s(amenity.max_party_size));
  const [daily, setDaily] = useState(s(amenity.per_home_daily_limit));
  const [weekly, setWeekly] = useState(s(amenity.per_home_weekly_limit));
  const [monthly, setMonthly] = useState(s(amenity.per_home_monthly_limit));
  const [activeLimit, setActiveLimit] = useState(s(amenity.per_home_active_limit));
  const [eligibility, setEligibility] = useState<AmenityEligibility>(amenity.eligibility);
  const [standing, setStanding] = useState(amenity.requires_good_standing);
  const [agreement, setAgreement] = useState(amenity.requires_agreement);
  const [capacity, setCapacity] = useState(s(amenity.capacity));
  const [checkIn, setCheckIn] = useState<number>(amenity.check_in_minute ?? 15 * 60);
  const [checkOut, setCheckOut] = useState<number>(amenity.check_out_minute ?? 11 * 60);
  const [minNights, setMinNights] = useState(s(amenity.min_nights));
  const [maxNights, setMaxNights] = useState(s(amenity.max_nights));
  const [vehicle, setVehicle] = useState(amenity.requires_vehicle);
  const [weeklyHours, setWeeklyHours] = useState(amenity.per_home_weekly_hours_limit ?? '');
  const [monthlyHours, setMonthlyHours] = useState(amenity.per_home_monthly_hours_limit ?? '');
  const save = useSave(onSave);
  const shared = amenity.booking_mode === 'shared_capacity';
  const overnight = amenity.booking_mode === 'overnight';

  useEffect(() => {
    setCapacity(s(amenity.capacity));
    setCheckIn(amenity.check_in_minute ?? 15 * 60);
    setCheckOut(amenity.check_out_minute ?? 11 * 60);
    setMinNights(s(amenity.min_nights));
    setMaxNights(s(amenity.max_nights));
    setVehicle(amenity.requires_vehicle);
    setWeeklyHours(amenity.per_home_weekly_hours_limit ?? '');
    setMonthlyHours(amenity.per_home_monthly_hours_limit ?? '');
    setIncrement(amenity.slot_increment_minutes);
    setMinDuration(amenity.min_duration_minutes);
    setMaxDuration(amenity.max_duration_minutes);
    setBuffer(s(amenity.buffer_minutes));
    setLeadDays(s(amenity.max_lead_days));
    setNoticeHours(s(amenity.min_notice_hours));
    setCancelHours(s(amenity.cancel_deadline_hours));
    setParty(s(amenity.max_party_size));
    setDaily(s(amenity.per_home_daily_limit));
    setWeekly(s(amenity.per_home_weekly_limit));
    setMonthly(s(amenity.per_home_monthly_limit));
    setActiveLimit(s(amenity.per_home_active_limit));
    setEligibility(amenity.eligibility);
    setStanding(amenity.requires_good_standing);
    setAgreement(amenity.requires_agreement);
  }, [amenity]);

  const durationOptions: number[] = [];
  for (let m = increment; m <= 24 * 60; m += increment) durationOptions.push(m);

  const ints = {
    buffer: optionalInt(buffer),
    leadDays: optionalInt(leadDays),
    noticeHours: optionalInt(noticeHours),
    cancelHours: optionalInt(cancelHours),
    party: optionalInt(party),
    daily: optionalInt(daily),
    weekly: optionalInt(weekly),
    monthly: optionalInt(monthly),
    activeLimit: optionalInt(activeLimit),
    capacity: optionalInt(capacity),
    minNights: optionalInt(minNights),
    maxNights: optionalInt(maxNights),
  };
  const hoursLimits = {
    weekly: optionalHours(weeklyHours),
    monthly: optionalHours(monthlyHours),
  };
  const badInt = Object.values(ints).some((v) => Number.isNaN(v));
  const badHours = Object.values(hoursLimits).some((v) => v === undefined);
  const durationsOk =
    minDuration <= maxDuration && minDuration % increment === 0 && maxDuration % increment === 0;
  const capacityOk = !shared || (ints.capacity !== null && !Number.isNaN(ints.capacity) && ints.capacity > 0);
  const nightsOk =
    !overnight ||
    ((ints.minNights === null || ints.minNights >= 1) &&
      (ints.maxNights === null || ints.maxNights >= 1) &&
      (ints.minNights === null || ints.maxNights === null || ints.minNights <= ints.maxNights));
  const valid = !badInt && !badHours && (overnight || durationsOk) && capacityOk && nightsOk;

  const hoursNum = (
    label: string,
    value: string,
    set: (v: string) => void,
    help: string,
    bad: boolean
  ) => (
    <label className={labelClass}>
      {label}
      <input
        inputMode="decimal"
        value={value}
        onChange={(e) => set(e.target.value.replace(/[^\d.]/g, ''))}
        placeholder="No limit"
        className={clsx(inputClass, 'w-32', bad && 'border-danger')}
      />
      <span className={clsx(helpClass, 'font-normal')}>{help}</span>
    </label>
  );

  const num = (
    label: string,
    value: string,
    set: (v: string) => void,
    help: string,
    bad: boolean
  ) => (
    <label className={labelClass}>
      {label}
      <input
        inputMode="numeric"
        value={value}
        onChange={(e) => set(e.target.value.replace(/\D/g, ''))}
        placeholder="No limit"
        className={clsx(inputClass, 'w-32', bad && 'border-danger')}
      />
      <span className={clsx(helpClass, 'font-normal')}>{help}</span>
    </label>
  );

  return (
    <form
      className="max-w-3xl space-y-6"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid || readOnly) return;
        save.run({
          ...amenityToUpdate(amenity),
          slot_increment_minutes: increment,
          min_duration_minutes: minDuration,
          max_duration_minutes: maxDuration,
          buffer_minutes: ints.buffer ?? 0,
          max_lead_days: ints.leadDays,
          min_notice_hours: ints.noticeHours,
          cancel_deadline_hours: ints.cancelHours,
          max_party_size: ints.party,
          per_home_daily_limit: ints.daily,
          per_home_weekly_limit: ints.weekly,
          per_home_monthly_limit: ints.monthly,
          per_home_active_limit: ints.activeLimit,
          eligibility,
          requires_good_standing: standing,
          requires_agreement: agreement,
          capacity: shared ? ints.capacity : amenity.capacity ?? null,
          check_in_minute: overnight ? checkIn : amenity.check_in_minute ?? null,
          check_out_minute: overnight ? checkOut : amenity.check_out_minute ?? null,
          min_nights: overnight ? ints.minNights : amenity.min_nights ?? null,
          max_nights: overnight ? ints.maxNights : amenity.max_nights ?? null,
          requires_vehicle: vehicle,
          per_home_weekly_hours_limit: hoursLimits.weekly ?? null,
          per_home_monthly_hours_limit: hoursLimits.monthly ?? null,
        });
      }}
    >
      <fieldset disabled={readOnly} className="space-y-6">
        {shared && (
          <section className="space-y-3">
            <h3 className="text-[13px] font-semibold text-fg">Shared capacity</h3>
            <p className={helpClass}>
              Several households can book the same time until the headcount is reached — each booking
              counts its whole party.
            </p>
            {num('Headcount per time slot', capacity, setCapacity, 'Required.', !capacityOk)}
          </section>
        )}
        {overnight && (
          <section className="space-y-3">
            <h3 className="text-[13px] font-semibold text-fg">Stays</h3>
            <p className={helpClass}>
              Booked by the night: a stay starts at check-in on the arrival day and ends at check-out on
              the departure day.
            </p>
            <div className="flex flex-wrap gap-3">
              <div className={labelClass}>
                Check-in from
                <FieldPicker
                  size="sm"
                  className="w-32"
                  label="Check-in from"
                  value={String(checkIn)}
                  onChange={(v) => setCheckIn(Number(v))}
                  disabled={readOnly}
                  options={timeOptions}
                />
              </div>
              <div className={labelClass}>
                Check-out by
                <FieldPicker
                  size="sm"
                  className="w-32"
                  label="Check-out by"
                  value={String(checkOut)}
                  onChange={(v) => setCheckOut(Number(v))}
                  disabled={readOnly}
                  options={timeOptions}
                />
              </div>
              {num('Fewest nights', minNights, setMinNights, 'Empty is one.', !nightsOk)}
              {num('Most nights', maxNights, setMaxNights, 'Empty is no limit.', !nightsOk)}
            </div>
            {!nightsOk && (
              <ErrorAlert>The fewest nights must be at least one and not more than the most.</ErrorAlert>
            )}
          </section>
        )}
        <section className={clsx('space-y-3', overnight && 'hidden')}>
          <h3 className="text-[13px] font-semibold text-fg">Time slots</h3>
          <div className="flex flex-wrap gap-3">
            <div className={labelClass}>
              Start times every
              <FieldPicker
                size="sm"
                className="w-36"
                label="Start times every"
                value={String(increment)}
                onChange={(v) => {
                  const next = Number(v);
                  setIncrement(next);
                  setMinDuration((m) => Math.max(next, Math.round(m / next) * next));
                  setMaxDuration((m) => Math.max(next, Math.round(m / next) * next));
                }}
                disabled={readOnly}
                options={Array.from(new Set([...INCREMENTS, amenity.slot_increment_minutes]))
                  .sort((a, b) => a - b)
                  .map((m) => ({ value: String(m), label: formatDuration(m) }))}
              />
            </div>
            <div className={labelClass}>
              Shortest booking
              <FieldPicker
                size="sm"
                className="w-36"
                label="Shortest booking"
                value={String(minDuration)}
                onChange={(v) => setMinDuration(Number(v))}
                disabled={readOnly}
                options={durationOptions.map((m) => ({ value: String(m), label: formatDuration(m) }))}
              />
            </div>
            <div className={labelClass}>
              Longest booking
              <FieldPicker
                size="sm"
                className="w-36"
                label="Longest booking"
                value={String(maxDuration)}
                onChange={(v) => setMaxDuration(Number(v))}
                disabled={readOnly}
                options={durationOptions.map((m) => ({ value: String(m), label: formatDuration(m) }))}
              />
            </div>
            {num('Cleanup buffer (minutes)', buffer, setBuffer, 'Kept free after each booking.', Number.isNaN(ints.buffer))}
          </div>
          {!durationsOk && (
            <ErrorAlert>
              The shortest booking must not be longer than the longest.
            </ErrorAlert>
          )}
        </section>

        <section className="space-y-3">
          <h3 className="text-[13px] font-semibold text-fg">When households can book</h3>
          <div className="flex flex-wrap gap-3">
            {num('Up to (days ahead)', leadDays, setLeadDays, 'How far ahead a booking may be made.', Number.isNaN(ints.leadDays))}
            {num('Notice needed (hours)', noticeHours, setNoticeHours, 'The latest a booking may be made before it starts.', Number.isNaN(ints.noticeHours))}
            {num('Free cancellation until (hours before)', cancelHours, setCancelHours, 'Cancelling later keeps the fee.', Number.isNaN(ints.cancelHours))}
            {num('Largest party', party, setParty, 'People, including the household.', Number.isNaN(ints.party))}
          </div>
        </section>

        <section className="space-y-3">
          <h3 className="text-[13px] font-semibold text-fg">Per household</h3>
          <p className={helpClass}>
            Counted per home, across everyone who lives there — pending requests count. Leave empty for no limit.
          </p>
          <div className="flex flex-wrap gap-3">
            {num('Per day', daily, setDaily, 'Bookings on one day.', Number.isNaN(ints.daily))}
            {num('Per week', weekly, setWeekly, 'Week start is in the reservation policy.', Number.isNaN(ints.weekly))}
            {num('Per month', monthly, setMonthly, 'Calendar month.', Number.isNaN(ints.monthly))}
            {num('Upcoming at once', activeLimit, setActiveLimit, 'Future bookings held at one time.', Number.isNaN(ints.activeLimit))}
          </div>
          {!overnight && (
            <div className="flex flex-wrap gap-3">
              {hoursNum('Hours per week', weeklyHours, setWeeklyHours, 'Total booked hours in a week.', hoursLimits.weekly === undefined)}
              {hoursNum('Hours per month', monthlyHours, setMonthlyHours, 'Total booked hours in a month.', hoursLimits.monthly === undefined)}
            </div>
          )}
        </section>

        <section className="space-y-3">
          <h3 className="text-[13px] font-semibold text-fg">Who can book</h3>
          <div className={labelClass}>
            Eligible
            <FieldPicker
              size="sm"
              className="w-56"
              label="Eligible"
              value={eligibility}
              onChange={(v) => setEligibility(v as AmenityEligibility)}
              disabled={readOnly}
              options={(['all_residents', 'owners_only'] as const).map((v) => ({
                value: v,
                label: ELIGIBILITY_LABELS[v],
              }))}
            />
          </div>
          <label className="flex items-start gap-2 text-[13px] text-fg-secondary">
            <input
              type="checkbox"
              className={clsx(checkboxClass, 'mt-0.5')}
              checked={standing}
              onChange={(e) => setStanding(e.target.checked)}
            />
            <span>
              Require good standing
              <span className={clsx(helpClass, 'block')}>
                Only applies once an Owner or Admin turns on the good-standing gate in the
                association’s reservation policy.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-[13px] text-fg-secondary">
            <input
              type="checkbox"
              className={clsx(checkboxClass, 'mt-0.5')}
              checked={agreement}
              onChange={(e) => setAgreement(e.target.checked)}
            />
            <span>
              Households must accept the agreement to book
              <span className={clsx(helpClass, 'block')}>
                Publish the agreement on the Agreement tab. Each booking records the version accepted.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-[13px] text-fg-secondary">
            <input
              type="checkbox"
              className={clsx(checkboxClass, 'mt-0.5')}
              checked={vehicle}
              onChange={(e) => setVehicle(e.target.checked)}
            />
            <span>
              Bookings must name the vehicle
              <span className={clsx(helpClass, 'block')}>
                For guest parking and similar: the household gives a licence plate, and management sees it
                on the booking.
              </span>
            </span>
          </label>
        </section>
      </fieldset>
      {!readOnly && <SaveRow {...save} disabled={!valid} />}
    </form>
  );
};

// ---------------------------------------------------------------------------
// Fee
// ---------------------------------------------------------------------------

export const AmenityFeeForm: React.FC<FormProps> = ({ amenity, readOnly, onSave }) => {
  const [feeType, setFeeType] = useState<AmenityFeeType>(amenity.fee_type);
  const [amount, setAmount] = useState(amenity.fee_amount ?? '');
  const [latePolicy, setLatePolicy] = useState<AmenityLateCancelPolicy>(amenity.late_cancel_policy);
  const [lateAmount, setLateAmount] = useState(amenity.late_cancel_amount ?? '');
  const save = useSave(onSave);
  const overnight = amenity.booking_mode === 'overnight';

  useEffect(() => {
    setFeeType(amenity.fee_type);
    setAmount(amenity.fee_amount ?? '');
    setLatePolicy(amenity.late_cancel_policy);
    setLateAmount(amenity.late_cancel_amount ?? '');
  }, [amenity]);

  const feeTypes: AmenityFeeType[] = overnight ? ['none', 'flat', 'per_night'] : ['none', 'flat', 'hourly'];
  const amountOk = feeType === 'none' || (isValidFee(amount) && !/^0*(\.0*)?$/.test(amount));
  const lateOk =
    feeType === 'none' ||
    latePolicy === 'keep_full' ||
    (isValidFee(lateAmount) && (latePolicy !== 'percent' || Number(lateAmount) <= 100));
  const valid = amountOk && lateOk;
  return (
    <form
      className="max-w-xl space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid || readOnly) return;
        const policy: AmenityLateCancelPolicy = feeType === 'none' ? 'keep_full' : latePolicy;
        save.run({
          ...amenityToUpdate(amenity),
          fee_type: feeType,
          fee_amount: feeType === 'none' ? null : amount.trim(),
          late_cancel_policy: policy,
          late_cancel_amount: policy === 'keep_full' ? null : lateAmount.trim(),
        });
      }}
    >
      <fieldset disabled={readOnly} className="space-y-4">
        <div className={labelClass}>
          Fee
          <FieldPicker
            size="sm"
            className="w-56"
            label="Fee"
            value={feeType}
            onChange={(v) => setFeeType(v as AmenityFeeType)}
            disabled={readOnly}
            options={Array.from(new Set([...feeTypes, amenity.fee_type])).map((t) => ({
              value: t,
              label: FEE_TYPE_LABELS[t],
            }))}
          />
        </div>
        {feeType !== 'none' && (
          <label className={labelClass}>
            {feeType === 'hourly' ? 'Per hour' : feeType === 'per_night' ? 'Per night' : 'Per booking'}
            <span className={clsx(inputClass, 'flex w-40 items-center', !amountOk && 'border-danger')}>
              <span className="text-fg-muted">$</span>
              <input
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
                placeholder="0.00"
                className="w-full border-0 bg-transparent p-0 pl-1 text-[13px] text-fg focus:outline-none focus:ring-0"
              />
            </span>
          </label>
        )}
        <p className={helpClass}>
          The fee is charged to the home’s account when a booking is approved — straight away for an
          amenity that confirms automatically — and is due on the day of use. It is reversed if the
          household cancels before the free-cancellation deadline (Booking limits tab) or if management
          cancels. Changing the fee here does not change bookings already made.
        </p>
        {feeType !== 'none' && (
          <section className="space-y-3 border-t border-line/60 pt-4">
            <h3 className="text-[13px] font-semibold text-fg">Cancelling after the deadline</h3>
            <div className={labelClass}>
              When a household cancels late
              <FieldPicker
                size="sm"
                className="w-80"
                label="When a household cancels late"
                value={latePolicy}
                onChange={(v) => setLatePolicy(v as AmenityLateCancelPolicy)}
                disabled={readOnly}
                options={(['keep_full', 'flat', 'percent'] as const).map((p) => ({
                  value: p,
                  label: LATE_CANCEL_POLICY_LABELS[p],
                }))}
              />
            </div>
            {latePolicy !== 'keep_full' && (
              <label className={labelClass}>
                {latePolicy === 'percent' ? 'Percent of the fee' : 'Late fee'}
                <span className={clsx(inputClass, 'flex w-40 items-center', !lateOk && 'border-danger')}>
                  {latePolicy === 'flat' && <span className="text-fg-muted">$</span>}
                  <input
                    inputMode="decimal"
                    value={lateAmount}
                    onChange={(e) => setLateAmount(e.target.value.replace(/[^\d.]/g, ''))}
                    placeholder={latePolicy === 'percent' ? '50' : '0.00'}
                    className="w-full border-0 bg-transparent p-0 pl-1 text-[13px] text-fg focus:outline-none focus:ring-0"
                  />
                  {latePolicy === 'percent' && <span className="text-fg-muted">%</span>}
                </span>
              </label>
            )}
            <p className={helpClass}>
              {latePolicy === 'keep_full'
                ? 'A late cancellation keeps the whole fee on the account.'
                : 'A late cancellation reverses the fee and charges this instead — never more than the fee itself.'}
            </p>
          </section>
        )}
      </fieldset>
      {!readOnly && <SaveRow {...save} disabled={!valid} />}
    </form>
  );
};
